import { parseLiteralReplacementSummary, type LiteralReplacementSummary } from "~shared/literalReplacementSummary";
import { sanitizeWriteCarouselSuggestionBody } from "~shared/writeCarouselSuggestionSanitize";

function manuscriptSuggestionHasRenderableContent(suggestion: string): boolean {
  if (/^\s*(?:[-*_]\s*){3,}$/.test(suggestion)) return false;
  return suggestion
    .replace(/[*_~`]/g, "")
    .replace(/^\s*(?:#{1,6}|>)\s*$/gm, "")
    .trim().length > 0;
}

export type ManuscriptChangePreviewV1 = {
  schemaVersion: 1;
  scope?: "selection" | "chapter" | "document" | "other";
  before: string;
  after: string;
  truncated: boolean;
  editCount: number;
  noOp: boolean;
  replacementText?: string;
  literalReplacements?: LiteralReplacementSummary[];
};

export type ManuscriptChangeMessageRef = {
  label?: string;
  scope?: ManuscriptChangePreviewV1["scope"];
  changeId: string;
  status: string;
  preview: ManuscriptChangePreviewV1 | null;
  /** Normalized target document id for ownership-scoped approve/decline. */
  documentId?: string;
  changeSetId?: string;
  proposalGroupId?: string;
  requestedOptionCount?: number;
  options?: Array<ManuscriptChangeMessageRef & { optionIndex: number; suggestionText?: string }>;
};

export type ManuscriptChangeOwnershipFields = {
  changeId: string;
  chatId: string;
  projectId: string;
  documentId: string;
};

/** Mirror of server normalizeManuscriptDocumentId for ownership payloads. */
export function normalizeManuscriptDocumentId(raw: string): string {
  const s = raw.trim();
  if (!s) return "";
  const pathMatch = s.match(/^\/project\/documents\/doc-([^/.]+)(?:\.md)?$/i);
  if (pathMatch) return pathMatch[1];
  const fileMatch = s.match(/^doc-([^/.]+)(?:\.md)?$/i);
  if (fileMatch) return fileMatch[1];
  const bareMd = s.match(/^([a-zA-Z0-9_-]+)\.md$/i);
  if (bareMd) return bareMd[1];
  return s;
}

export function parseManuscriptChangeMessageRef(
  raw: unknown,
): ManuscriptChangeMessageRef | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const rec = raw as Record<string, unknown>;
  const changeId = typeof rec.changeId === "string" ? rec.changeId.trim() : "";
  const status = typeof rec.status === "string" ? rec.status.trim() : "";
  if (!changeId || !status) return null;

  let preview: ManuscriptChangePreviewV1 | null = null;
  if (rec.preview && typeof rec.preview === "object" && !Array.isArray(rec.preview)) {
    const p = rec.preview as Record<string, unknown>;
    if (p.schemaVersion === 1 || p.version === 1) {
      preview = {
        ...(["selection", "chapter", "document", "other"].includes(String(p.scope))
          ? { scope: p.scope as ManuscriptChangePreviewV1["scope"] } : {}),
        ...(parseLiteralReplacementSummary(p.literalReplacements)
          ? { literalReplacements: parseLiteralReplacementSummary(p.literalReplacements) } : {}),
        schemaVersion: 1,
        before: String(p.before ?? ""),
        after: String(p.after ?? ""),
        truncated: p.truncated === true,
        editCount: Number(p.editCount ?? 0),
        noOp: p.noOp === true,
        ...(typeof p.replacementText === "string" && p.replacementText.trim()
          ? { replacementText: p.replacementText }
          : {}),
      };
    }
  }

  const documentIdRaw =
    typeof rec.documentId === "string" ? rec.documentId.trim() : "";
  const documentId = documentIdRaw
    ? normalizeManuscriptDocumentId(documentIdRaw)
    : undefined;

  const options = Array.isArray(rec.options)
    ? rec.options.flatMap((rawOption, index) => {
        const parsed = parseManuscriptChangeMessageRef(rawOption);
        if (!parsed) return [];
        const optionIndex = rawOption && typeof rawOption === "object"
          ? Number((rawOption as { optionIndex?: unknown }).optionIndex ?? index)
          : index;
        const suggestionText = rawOption && typeof rawOption === "object"
          && typeof (rawOption as { suggestionText?: unknown }).suggestionText === "string"
          ? String((rawOption as { suggestionText: string }).suggestionText).trim()
          : "";
        return [{
          ...parsed,
          optionIndex,
          ...(suggestionText ? { suggestionText } : {}),
        }];
      })
    : [];
  return {
    ...(["selection", "chapter", "document", "other"].includes(String(rec.scope))
      ? { scope: rec.scope as ManuscriptChangePreviewV1["scope"] } : {}),
    ...(typeof rec.label === "string" && rec.label.trim() ? { label: rec.label.trim() } : {}),
    changeId,
    status,
    preview,
    ...(documentId ? { documentId } : {}),
    ...(typeof rec.changeSetId === "string" && rec.changeSetId.trim()
      ? { changeSetId: rec.changeSetId.trim() }
      : {}),
    ...(typeof rec.proposalGroupId === "string" && rec.proposalGroupId.trim()
      ? { proposalGroupId: rec.proposalGroupId.trim() }
      : {}),
    ...(typeof rec.requestedOptionCount === "number" && Number.isInteger(rec.requestedOptionCount)
      ? { requestedOptionCount: Number(rec.requestedOptionCount) }
      : {}),
    ...(options.length > 0 ? { options } : {}),
  };
}

const MANUSCRIPT_CHANGE_CAROUSEL_STATUSES = new Set([
  "awaiting_consent",
  "preview_only",
  "ready_to_apply",
  "applying",
  "needs_review_again",
  "applied",
  "visible",
  "partially_applied",
]);

export function manuscriptChangeIsReadOnlyHistory(raw: unknown): boolean {
  const parsed = parseManuscriptChangeMessageRef(raw);
  return parsed?.status === "declined" || parsed?.status === "superseded";
}

export function manuscriptChangeUsesCompactResult(raw: unknown, includeReadOnlyHistory = false): boolean {
  const parsed = parseManuscriptChangeMessageRef(raw);
  const scope = parsed?.scope ?? parsed?.preview?.scope;
  // Declining insertion retires approval, not the saved prose. Keep its card
  // for whole-document proposals too; missing previews still use a receipt.
  if (includeReadOnlyHistory && manuscriptChangeIsReadOnlyHistory(parsed)
    && (parsed?.preview || parsed?.options?.some(option => option.preview || option.suggestionText))) return false;
  // Alternative proposals require a visible choice even when their target was
  // resolved from a document rather than an editor selection. Keep the same
  // cards after application so the selected option retains its durable Undo.
  // Keep the post-Undo dismissal; other terminal outcomes need a compact result.
  if ((parsed?.requestedOptionCount ?? 1) > 1
    && (parsed?.status === "generating" || parsed?.status === "undone" || MANUSCRIPT_CHANGE_CAROUSEL_STATUSES.has(parsed?.status ?? ""))) return false;
  // Unapplied proposals must show their prose before approval, regardless of
  // scope: the server may omit chat text in favor of the suggestion card.
  if (["preview_only", "awaiting_consent", "ready_to_apply", "needs_review_again"].includes(parsed?.status ?? "")) return false;
  // Keep saved proposal prose visible through application and reload. A receipt
  // is only a fallback when no card text is available, not a replacement for it.
  if (MANUSCRIPT_CHANGE_CAROUSEL_STATUSES.has(parsed?.status ?? "")
    && (parsed?.preview?.replacementText || parsed?.preview?.after
      || parsed?.options?.some(option => option.suggestionText || option.preview?.replacementText || option.preview?.after))) return false;
  return scope !== undefined && scope !== "selection";
}

export function manuscriptChangeCanUseRewriteCarousel(raw: unknown, includeReadOnlyHistory = false): boolean {
  const parsed = parseManuscriptChangeMessageRef(raw);
  return Boolean(parsed && !manuscriptChangeUsesCompactResult(parsed, includeReadOnlyHistory) && (MANUSCRIPT_CHANGE_CAROUSEL_STATUSES.has(parsed.status) || (includeReadOnlyHistory && manuscriptChangeIsReadOnlyHistory(parsed))));
}

/** True while a typed suggestion card is still generating or not yet renderable. */
export function manuscriptChangeSuggestionCardPending(
  manuscriptChange: unknown,
  hasRenderableCarousel: boolean,
): boolean {
  const parsed = parseManuscriptChangeMessageRef(manuscriptChange);
  if (!parsed || manuscriptChangeUsesCompactResult(parsed)) return false;
  if (parsed.status === "generating") return true;
  if (manuscriptChangeStatusShowsDurableUndo(parsed.status)) return false;
  if (manuscriptChangeCanUseRewriteCarousel(parsed)) {
    return !hasRenderableCarousel;
  }
  return false;
}

const PROPOSE_MANUSCRIPT_CHANGE_SUGGESTION_CARD_STATUSES = new Set([
  "awaiting_consent",
  "preview_only",
  "ready_to_apply",
]);

function readProposeManuscriptChangeResultStatus(result: unknown): string | null {
  if (!result || typeof result !== "object") return null;
  const data = (result as { data?: unknown }).data;
  if (!data || typeof data !== "object") return null;
  const status = (data as { status?: unknown }).status;
  return typeof status === "string" ? status : null;
}

/** True when persisted tool metadata owns a suggestion card that is not renderable yet. */
export function proposeManuscriptChangeToolAwaitingSuggestionCard(
  toolExecutions: unknown,
  hasRenderableCarousel: boolean,
): boolean {
  if (!Array.isArray(toolExecutions)) return false;
  for (let index = toolExecutions.length - 1; index >= 0; index -= 1) {
    const execution = toolExecutions[index];
    if (!execution || typeof execution !== "object") continue;
    if ((execution as { name?: unknown }).name !== "propose_manuscript_change") continue;
    const resultData = ((execution as { result?: { data?: unknown } }).result)?.data;
    if (manuscriptChangeUsesCompactResult(resultData)) return false;
    const status = readProposeManuscriptChangeResultStatus(
      (execution as { result?: unknown }).result,
    );
    if (status === "generating") return true;
    if (status && PROPOSE_MANUSCRIPT_CHANGE_SUGGESTION_CARD_STATUSES.has(status)) {
      return !hasRenderableCarousel;
    }
    return false;
  }
  return false;
}

const MANUSCRIPT_CHANGE_DURABLE_UNDO_STATUSES = new Set([
  "applied",
  "visible",
  "partially_applied",
  "partially_undone",
  "undoing",
  "applying",
]);

export function manuscriptChangeStatusShowsDurableUndo(status: string): boolean {
  return MANUSCRIPT_CHANGE_DURABLE_UNDO_STATUSES.has(status);
}

export function manuscriptChangeRefShowsDurableUndo(
  ref: ManuscriptChangeMessageRef | null | undefined,
): boolean {
  return Boolean(ref?.changeSetId && manuscriptChangeStatusShowsDurableUndo(ref.status));
}

/** Persisted option index that should render as the applied Undo card. */
export function manuscriptChangeAppliedSuggestionIndex(
  raw: unknown,
  rewriteSuggestions?: unknown,
): number | null {
  const parsed = parseManuscriptChangeMessageRef(raw);
  if (!parsed?.changeSetId || !manuscriptChangeStatusShowsDurableUndo(parsed.status)) {
    return null;
  }
  const appliedOption = parsed.options?.find((option) =>
    manuscriptChangeStatusShowsDurableUndo(option.status),
  );
  if (appliedOption && parsed.options?.length) {
    const visibleOptions = parsed.options.filter((option) => Boolean(stripInternalManuscriptMarkup(
      option.suggestionText
      ?? option.preview?.replacementText
      ?? option.preview?.after
      ?? "",
    )));
    if (visibleOptions.length > 0) {
      const visibleIndex = visibleOptions.findIndex((option) => option.changeId === appliedOption.changeId);
      return visibleIndex >= 0 ? visibleIndex : null;
    }
    if (Array.isArray(rewriteSuggestions)) {
      const visibleRewriteIndexes = rewriteSuggestions.flatMap((suggestion, index) =>
        typeof suggestion === "string"
        && manuscriptSuggestionHasRenderableContent(sanitizeWriteCarouselSuggestionBody(suggestion).trim())
          ? [index]
          : []);
      const visibleIndex = visibleRewriteIndexes.indexOf(appliedOption.optionIndex);
      return visibleIndex >= 0 ? visibleIndex : null;
    }
  }
  const hasSingleCardProse = Boolean(
    parsed.preview?.replacementText?.trim() || parsed.preview?.after?.trim(),
  );
  if (!parsed.options?.length && hasSingleCardProse) return 0;
  return null;
}

function stripInternalManuscriptMarkup(value: string): string {
  return value
    .replace(/<\/?(?:insertion|inserted|deletion|deleted|insert|annotation|mark|highlight)(?:\s[^>]*)?>/gi, "")
    .replace(/<\/?inserted>/gi, "")
    .replace(/<annotation\b[^>]*>/gi, "")
    .replace(/<\/annotation>/gi, "")
    .trim();
}

/** Drop hollow model-authored Before/After blocks when prose is shown elsewhere. */
export function stripRedundantManuscriptChangeBeforeAfterProse(text: string): string {
  let trimmed = String(text ?? "").trim();
  if (!trimmed) return trimmed;

  trimmed = trimmed.replace(
    /\n*\*\*Before:\*\*[\s\S]*?\*\*After:\*\*[\s\S]*?(?=(?:\n\n(?![*\s])|$))/gi,
    (block) => {
      const inner = block
        .replace(/\*\*Before:\*\*/gi, "")
        .replace(/\*\*After:\*\*/gi, "")
        .replace(/[*\s_\-—:`]/g, "");
      return inner.length > 0 ? block : "";
    },
  );

  trimmed = trimmed
    .replace(/^\s*\*{2,}\s*$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  const blocks = trimmed.split(/\n\s*\n/);
  const emptySuggestionBlocks = blocks.map((block) => {
    const bullet = block.match(/^\s*[-*+]\s+([\s\S]*)$/);
    if (!bullet) return false;
    const body = sanitizeWriteCarouselSuggestionBody(bullet[1] ?? "").trim();
    return !manuscriptSuggestionHasRenderableContent(body);
  });
  if (!emptySuggestionBlocks.some(Boolean)) return trimmed;

  return blocks
    .filter((block, index) => {
      if (emptySuggestionBlocks[index]) return false;
      if (!/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/.test(block)) return true;
      return !emptySuggestionBlocks[index - 1] && !emptySuggestionBlocks[index + 1];
    })
    .join("\n\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function normalizeManuscriptChangeSuggestions(
  raw: unknown,
  fallback: string | null,
): string[] | null {
  const suggestions = Array.isArray(raw)
    ? raw
        .filter((entry): entry is string => typeof entry === "string")
        .map((entry) => sanitizeWriteCarouselSuggestionBody(entry).trim())
        .filter(manuscriptSuggestionHasRenderableContent)
    : [];
  if (suggestions.length > 0) return suggestions;

  const normalizedFallback = sanitizeWriteCarouselSuggestionBody(fallback ?? "").trim();
  return manuscriptSuggestionHasRenderableContent(normalizedFallback) ? [normalizedFallback] : null;
}

/** Derive display prose for the existing rewrite carousel without exposing internal tags. */
export function manuscriptChangeSuggestionText(params: {
  manuscriptChange: unknown;
  operation: unknown;
  /** Include saved declined and superseded drafts as read-only history. */
  includeReadOnlyHistory?: boolean;
}): string | null {
  const parsed = parseManuscriptChangeMessageRef(params.manuscriptChange);
  if (!parsed || !manuscriptChangeCanUseRewriteCarousel(parsed, params.includeReadOnlyHistory)) return null;

  const operation =
    params.operation && typeof params.operation === "object" && !Array.isArray(params.operation)
      ? params.operation as Record<string, unknown>
      : {};
  const kind = typeof operation.kind === "string" ? operation.kind : "";
  const operationText =
    kind === "replace_all" && typeof operation.content === "string"
      ? operation.content
      : ["insert_before", "insert_after", "append", "prepend"].includes(kind)
        && typeof operation.text === "string"
        ? operation.text
        : "";
  const source = operationText
    || parsed.preview?.replacementText
    || parsed.preview?.after
    || "";
  const cleaned = stripInternalManuscriptMarkup(source);
  return cleaned || null;
}

export function manuscriptChangeSuggestionTexts(params: {
  manuscriptChange: unknown;
  operation: unknown;
  /** Include saved declined and superseded drafts as read-only history. */
  includeReadOnlyHistory?: boolean;
}): string[] {
  const entries = manuscriptChangeSuggestionEntries({
    manuscriptChange: params.manuscriptChange,
    includeReadOnlyHistory: params.includeReadOnlyHistory,
  });
  if (entries.length > 0) return entries.map((entry) => entry.text);
  const single = manuscriptChangeSuggestionText(params);
  return single ? [single] : [];
}

export function manuscriptChangeSuggestionEntries(params: {
  manuscriptChange: unknown;
  rewriteSuggestions?: unknown;
  includeReadOnlyHistory?: boolean;
}): Array<{ text: string; optionId: string; documentId?: string; label?: string }> {
  const parsed = parseManuscriptChangeMessageRef(params.manuscriptChange);
  if (!parsed || manuscriptChangeUsesCompactResult(parsed, params.includeReadOnlyHistory)) return [];
  if (parsed.options?.length) {
    const optionEntries = parsed.options.flatMap((option) => {
      const source = stripInternalManuscriptMarkup(
        option.suggestionText
        ?? option.preview?.replacementText
        ?? option.preview?.after
        ?? "",
      );
      return source
        ? [{
            text: source,
            optionId: option.changeId,
            ...(option.label ? { label: option.label } : {}),
            ...(option.documentId ? { documentId: option.documentId } : {}),
          }]
        : [];
    });
    if (optionEntries.length > 0) return optionEntries;
    if (Array.isArray(params.rewriteSuggestions)) {
      return params.rewriteSuggestions.flatMap((suggestion, index) => {
        if (typeof suggestion !== "string") return [];
        const text = sanitizeWriteCarouselSuggestionBody(suggestion).trim();
        if (!manuscriptSuggestionHasRenderableContent(text)) return [];
        const option = parsed.options?.find((candidate) => candidate.optionIndex === index);
        return option
          ? [{
              text,
              optionId: option.changeId,
            ...(option.label ? { label: option.label } : {}),
              ...(option.documentId ? { documentId: option.documentId } : {}),
            }]
          : [];
      });
    }
  }
  return [];
}

export function resolveManuscriptChangeDocumentId(params: {
  messageMetadata: Record<string, unknown>;
  activeDocumentId?: string | null;
}): string | undefined {
  const meta = params.messageMetadata;

  // Prefer the typed change's own target document over open-doc fallbacks.
  const manuscriptChange = meta.manuscriptChange;
  if (
    manuscriptChange
    && typeof manuscriptChange === "object"
    && !Array.isArray(manuscriptChange)
  ) {
    const fromChange = (manuscriptChange as { documentId?: unknown }).documentId;
    if (typeof fromChange === "string" && fromChange.trim()) {
      return normalizeManuscriptDocumentId(fromChange);
    }
  }

  const fromMeta =
    typeof meta.documentId === "string" ? meta.documentId.trim() : "";
  if (fromMeta) return normalizeManuscriptDocumentId(fromMeta);

  const placement = meta.placementArtifact;
  if (placement && typeof placement === "object" && !Array.isArray(placement)) {
    const documentId = (placement as { documentId?: unknown }).documentId;
    if (typeof documentId === "string" && documentId.trim()) {
      return normalizeManuscriptDocumentId(documentId);
    }
  }

  const fromComment =
    typeof meta.referencedCommentDocumentId === "string"
      ? meta.referencedCommentDocumentId.trim()
      : "";
  if (fromComment) return normalizeManuscriptDocumentId(fromComment);

  const active = params.activeDocumentId?.trim();
  return active ? normalizeManuscriptDocumentId(active) : undefined;
}

export function extractProposeManuscriptChangeOperation(
  toolExecutions: unknown,
): unknown | undefined {
  if (!Array.isArray(toolExecutions)) return undefined;
  for (let i = toolExecutions.length - 1; i >= 0; i -= 1) {
    const execution = toolExecutions[i];
    if (!execution || typeof execution !== "object") continue;
    const name = (execution as { name?: unknown }).name;
    if (name !== "propose_manuscript_change") continue;
    const args = (execution as { arguments?: unknown }).arguments;
    if (!args || typeof args !== "object" || Array.isArray(args)) continue;
    const operation = (args as { operation?: unknown }).operation;
    if (operation && typeof operation === "object" && !Array.isArray(operation)) {
      return operation;
    }
  }
  return undefined;
}

/** A finalized receipt replaces provisional prose even when the assistant body is empty. */
export function messageHasFinalManuscriptChange(message: { metadata?: unknown } | null | undefined): boolean {
  const metadata = (message?.metadata ?? {}) as Record<string, unknown>;
  const isFinal = (value: unknown) => {
    const receipt = parseManuscriptChangeMessageRef(value);
    return Boolean(receipt && receipt.status !== "generating");
  };
  if (isFinal(metadata.manuscriptChange)) return true;
  return Array.isArray(metadata.toolExecutions) && metadata.toolExecutions.some(execution =>
    execution?.name === "propose_manuscript_change"
    && execution.result?.success !== false
    && isFinal(execution.result?.data));
}
